/**
 * Guide-frame fill detection for BulkScanner.
 * Ported from CardResearch - multi-signal per-edge detection.
 */

/**
 * Check if a card is present inside the guide frame.
 * Uses three signals combined per edge:
 *  1. Multi-depth brightness contrast (handles toploaders where card is inset)
 *  2. Gradient strength along edge (detects card/toploader borders)
 *  3. Texture variance (card artwork vs uniform background)
 *
 * @param {Uint8ClampedArray} pixels - RGBA pixel data from analysis canvas
 * @param {object} guide - { x, y, w, h } in analysis pixel coords
 * @param {number} w - analysis image width
 * @param {number} h - analysis image height
 * @returns {{ detected: boolean, edgeCount: number, coverage: object }}
 */
export function detectCardInGuide(pixels, guide, w, h) {
  const EDGES_NEEDED = 3;
  const EDGE_THRESH = 20;

  function getBrightness(px, py) {
    if (px < 0 || px >= w || py < 0 || py >= h) return 0;
    const i = (py * w + px) * 4;
    return 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
  }

  function bandStats(x1, y1, x2, y2) {
    let sum = 0, sumSq = 0, count = 0;
    const minX = Math.max(0, Math.round(x1));
    const maxX = Math.min(w - 1, Math.round(x2));
    const minY = Math.max(0, Math.round(y1));
    const maxY = Math.min(h - 1, Math.round(y2));
    for (let py = minY; py <= maxY; py++) {
      for (let px = minX; px <= maxX; px++) {
        const b = getBrightness(px, py);
        sum += b;
        sumSq += b * b;
        count++;
      }
    }
    if (count === 0) return { mean: 0, variance: 0 };
    const mean = sum / count;
    return { mean, variance: sumSq / count - mean * mean };
  }

  function gradientAlongEdge(axis, fixed, start, end) {
    let sum = 0, count = 0;
    const step = 2;
    if (axis === 'h') {
      for (let x = Math.round(start); x <= Math.round(end); x += step) {
        const b1 = getBrightness(x, Math.round(fixed) - 1);
        const b2 = getBrightness(x, Math.round(fixed) + 1);
        sum += Math.abs(b2 - b1);
        count++;
      }
    } else {
      for (let y = Math.round(start); y <= Math.round(end); y += step) {
        const b1 = getBrightness(Math.round(fixed) - 1, y);
        const b2 = getBrightness(Math.round(fixed) + 1, y);
        sum += Math.abs(b2 - b1);
        count++;
      }
    }
    return count > 0 ? sum / count : 0;
  }

  const gx = guide.x, gy = guide.y, gw = guide.w, gh = guide.h;
  const OUTER = 8;
  const INNER_DEPTHS = [
    { offset: 0, width: 6 },
    { offset: 8, width: 8 },
    { offset: 18, width: 10 },
  ];

  function edgeScore(axis, edgePos, innerDir, outerDir, bandStart, bandEnd) {
    let outerX1, outerY1, outerX2, outerY2;
    if (axis === 'h') {
      outerX1 = bandStart; outerX2 = bandEnd;
      if (outerDir < 0) { outerY1 = edgePos - OUTER; outerY2 = edgePos; }
      else { outerY1 = edgePos; outerY2 = edgePos + OUTER; }
    } else {
      outerY1 = bandStart; outerY2 = bandEnd;
      if (outerDir < 0) { outerX1 = edgePos - OUTER; outerX2 = edgePos; }
      else { outerX1 = edgePos; outerX2 = edgePos + OUTER; }
    }
    const outer = bandStats(outerX1, outerY1, outerX2, outerY2);

    let bestContrast = 0;
    let bestVarianceDiff = 0;
    for (const { offset, width } of INNER_DEPTHS) {
      let ix1, iy1, ix2, iy2;
      if (axis === 'h') {
        ix1 = bandStart; ix2 = bandEnd;
        if (innerDir < 0) { iy1 = edgePos - offset - width; iy2 = edgePos - offset; }
        else { iy1 = edgePos + offset; iy2 = edgePos + offset + width; }
      } else {
        iy1 = bandStart; iy2 = bandEnd;
        if (innerDir < 0) { ix1 = edgePos - offset - width; ix2 = edgePos - offset; }
        else { ix1 = edgePos + offset; ix2 = edgePos + offset + width; }
      }
      const inner = bandStats(ix1, iy1, ix2, iy2);
      const contrast = Math.abs(inner.mean - outer.mean);
      if (contrast > bestContrast) bestContrast = contrast;
      const vDiff = inner.variance - outer.variance;
      if (vDiff > bestVarianceDiff) bestVarianceDiff = vDiff;
    }

    const grad = gradientAlongEdge(axis, edgePos, bandStart, bandEnd);
    const textureBonus = bestVarianceDiff > 200 ? 5 : bestVarianceDiff > 50 ? 2 : 0;
    return bestContrast + grad * 0.5 + textureBonus;
  }

  const topScore = edgeScore('h', gy, 1, -1, gx, gx + gw);
  const botScore = edgeScore('h', gy + gh, -1, 1, gx, gx + gw);
  const leftScore = edgeScore('v', gx, 1, -1, gy, gy + gh);
  const rightScore = edgeScore('v', gx + gw, -1, 1, gy, gy + gh);

  const scores = [topScore, botScore, leftScore, rightScore];
  const edgeCount = scores.filter(s => s > EDGE_THRESH).length;

  return {
    detected: edgeCount >= EDGES_NEEDED,
    edgeCount,
    coverage: {
      top: Math.round(topScore),
      bottom: Math.round(botScore),
      left: Math.round(leftScore),
      right: Math.round(rightScore),
    },
  };
}
